var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable @typescript-eslint/no-use-before-define */
import axios from 'axios';
import isBrowser from './is-browser.js';
import tap from './tap.js';
import tapRejection from './tap-rejection.js';
const shouldRedact = () => process.env.CLIENT_LOG_ENVIRONMENT === 'production';
const logResponse = ({ data, status, statusText, headers, config }) => {
    !isBrowser() &&
        console.info(`
[${config.method}] ${config.url}
[headers]: ${JSON.stringify(scrubHeaders(config.headers), null, 2)}
[body]: ${shouldRedact() ? 'REDACTED' : `${JSON.stringify(config.data, null, 2)}`}
->
${`${status}`} ${statusText}
headers: ${JSON.stringify(scrubHeaders(headers), null, 2)}
response: ${shouldRedact() ? 'REDACTED' : `${JSON.stringify(data, null, 2)}`}
`);
};
const logError = (error) => {
    if (isBrowser())
        return;
    const { response, response: { data, status, headers } = {}, message, name, config } = error;
    if (response) {
        console.error(`
  ${name}: ${message}
  [${config.method}] ${config.url}
  [headers]: ${JSON.stringify(scrubHeaders(config.headers), null, 2)}
  [body]: ${shouldRedact() ? 'REDACTED' : `${JSON.stringify(config.data, null, 2)}`}
  ->
  ${`${status}`}
  headers: ${JSON.stringify(scrubHeaders(headers), null, 2)}
  response: ${shouldRedact() ? 'REDACTED' : `${JSON.stringify(data, null, 2)}`}
  `);
    }
    else {
        return console.error(error.message || error);
    }
};
/**
 * Converts Headers to a plain object.
 * @param {Headers} headers See https://developer.mozilla.org/en-US/docs/Web/API/Headers
 * @returns {Object} Plain object representation of headers.
 */
const headersAsObject = (headers) => {
    const headersAsObj = {};
    headers.forEach((value, name) => {
        headersAsObj[name] = value;
    });
    return headersAsObj;
};
/**
 * Use this to wrap a fetch compatible library (cross-fetch, native fetch, etc) with Nav's logging capabilities. This
 * is especially useful for working with Apollo Client, which requires fetch and can't use our `request()` function.
 * @param {Function} fetch Any fetch compatible library
 * @returns {Function} A wrapped version of the fetch library that will leverage `logResponse()` and `logError()`.
 */
export const fetchWithLogging = (fetch) => (url, options) => __awaiter(void 0, void 0, void 0, function* () {
    const response = yield fetch(url, options);
    try {
        // Break apart the response so we can log it
        const data = yield response.text();
        const { status, statusText, headers } = response;
        logResponse({
            data: JSON.parse(data),
            status,
            statusText,
            headers: headersAsObject(headers),
            config: { url, data: options.body, headers: options.headers, method: options.method },
        });
        // Make the response "fetch compatible" again
        return new Response(data, {
            status,
            statusText,
            headers,
        });
    }
    catch (error) {
        logError(error);
        throw error;
    }
});
const removeSignatureFromToken = (token) => token.split('.').slice(0, -1).concat('REDACTED').join('.');
const scrubHeaders = (headers) => Object.fromEntries(Object.entries(headers)
    // filter headers we know we want to suppress
    .map(([key, val]) => [key, shouldRedact() && key.match(/api-key/i) ? 'REDACTED' : val])
    .map(([key, val]) => [key, shouldRedact() && key.match(/authorization/i) ? removeSignatureFromToken(val) : val]));
export default (_a) => {
    var { authJWT, body, data = body, headers, method = 'GET', uri = '', url = uri } = _a, axiosArgs = __rest(_a, ["authJWT", "body", "data", "headers", "method", "uri", "url"]);
    return axios(Object.assign({ method,
        url, headers: Object.assign(Object.assign(Object.assign(Object.assign({}, (authJWT && {
            Authorization: `Bearer ${authJWT}`,
            'x-authentication-jwt': authJWT,
        })), (!isBrowser() && {
            'accept-encoding': 'utf-8',
            'user-agent': 'nav',
        })), { accept: 'application/json', 'content-type': 'application/json' }), headers), data }, axiosArgs))
        .then(tap(logResponse))
        .catch(tapRejection(logError));
};
//# sourceMappingURL=request.js.map